import {
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import WSButton from "../../Components/Button/WSButton";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";

const EPrint: React.FC = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { showMessage } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("estimates", Method.GET, null, id);
        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      getDataById();
    }
  }, [id]);

  // 🖨️ Print Handler
  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return <NotFound />;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={4}
      bgcolor="grey.100"
      minHeight="100vh"
    >
      <WSButton
        id="printBtn"
        size="small"
        label="Print Estimation"
        onClick={handlePrint}
      />
      <Box
        ref={contentRef}
        mt={2}
        sx={{ p: 4, width: "100%", maxWidth: "800px", bgcolor: "white" }}
      >
        {/* Company Header */}
        <Box textAlign="center" borderBottom={1} pb={2} mb={2}>
          <Typography variant="h4" fontWeight="bold" color="error">
            MHB INTERLOCK
          </Typography>
          <Typography variant="subtitle1">Make Home Beautiful</Typography>
          <Typography variant="body2">
            E.mail: mhbinterlock@gmail.com | mhb-Industries
          </Typography>
          <Typography variant="body2">
            NADUKANI, PALLIVAYAL P.O 670142
          </Typography>
          <Typography variant="body2">PH: 9747535535, 9544541114/17</Typography>
        </Box>

        {/* Estimate Details */}
        <Box textAlign="left" mb={3}>
          <Typography variant="h6" color="primary">
            To,
          </Typography>
          <Typography>{data.customerName}</Typography>
          <Typography>Site at {data.address}.</Typography>
        </Box>

        {/* Estimation Details */}
        <Box textAlign="center" mb={3}>
          <Typography variant="h5">Estimation</Typography>
          <Typography variant="body2">
            Valid Till: {new Date(data.validTill).toLocaleDateString()}
          </Typography>
        </Box>

        <Box mb={3}>
          <Typography variant="h6">Dear sir/madam,</Typography>
          <Typography variant="body2">
            {data.subject}
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.300" }}>
                <TableCell>Description</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="center">Unit</TableCell>
                <TableCell align="center">Unit Price</TableCell>
                <TableCell align="center">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.estimateItems.map((item: any) => (
                <TableRow key={item.id}>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="center">{item.unit}</TableCell>
                  <TableCell align="center">{item.unitPrice}</TableCell>
                  <TableCell align="center">{item.totalPrice}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Terms and Condtion */}

        <Box mt={2}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions.
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            ➤ {data.termsAndConditions}
          </Typography>
        </Box>

        {/* Payment Terms */}
        <Box mt={4}>
          <Typography variant="h6" color="primary">
            Mode of Payment:
          </Typography>
          <ul>
            <li>
              <Typography>
                <b>30%</b> of total value of work on order confirmation.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>60%</b> of total value of work on delivery of material to
                site.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>10%</b> balance after completion of work.
              </Typography>
            </li>
          </ul>
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box>
            <Typography fontWeight="bold">Accepted:</Typography>
            <Typography>Name of Client: ___________________</Typography>
            <Typography>Witnesses:</Typography>
            <Typography>1. ___________________</Typography>
            <Typography>2. ___________________</Typography>
          </Box>
          <Box textAlign="right">
            <Typography>Thanks & Regards,</Typography>
            <Typography fontWeight="bold">JOSEPH PU</Typography>
            <Typography>Managing Director</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EPrint;
